<script setup lang="ts">
import type { ContentNavigationItem } from '@nuxt/content'
import { getSuggestionCategory, type Suggestion } from '~~/shared/utils/suggestions'

// Help center hub: the docs sections, the suggestions board, and contact
// options. Prerendered: the docs navigation is read at build time (serverless
// has no content database at runtime, see layouts/docs.vue). The popular
// suggestions strip is live data, so it fetches client-side.

const title = 'Help center: guides and feature suggestions'
const description = 'Guides for every part of your mychar.gg page, from links and customization to integrations and billing. Suggest features and vote on what we build next.'

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description
})

defineOgImageComponent('Saas', {
  title: 'Help center',
  description: 'Guides for every part of your page, plus feature suggestions.'
})

const siteUrl = useRuntimeConfig().public.siteUrl
const discordInviteUrl = useRuntimeConfig().public.discordInviteUrl

useSchemaOrg([
  defineWebPage({ '@type': 'CollectionPage' }),
  defineBreadcrumb({
    itemListElement: [
      { name: 'Home', item: siteUrl },
      { name: 'Help center', item: `${siteUrl}/help` }
    ]
  })
])

const PAGES_PER_SECTION = 4

const { data: navigation } = await useAsyncData('help-docs-navigation', () => queryCollectionNavigation('docs', ['description']).catch(() => []), {
  transform: data => data?.find(item => item.path === '/docs')?.children || [],
  default: () => []
})

// Each docs folder is a section; its index page supplies the icon and summary
const sections = computed(() => navigation.value.map((section: ContentNavigationItem) => {
  const children = section.children ?? []
  const index = children.find(child => child.path === section.path)
  return {
    title: section.title,
    path: section.path,
    icon: (index?.icon as string | undefined) || 'i-lucide-book-open',
    description: (index?.description as string | undefined) ?? '',
    pages: children.filter(child => child.path !== section.path)
  }
}))

const { data: popular, status: popularStatus } = useFetch<{ suggestions: Suggestion[] }>('/api/suggestions', {
  query: { sort: 'top', limit: 3 },
  server: false,
  lazy: true
})
</script>

<template>
  <div>
    <UPageHero
      title="Help center"
      description="Guides for every part of your page, and a place to suggest what we build next."
      :ui="{ container: 'pt-28 pb-12 sm:pt-32 sm:pb-16 lg:pt-36 lg:pb-16' }"
    >
      <template #links>
        <UContentSearchButton
          :collapsed="false"
          label="Search the docs"
          size="lg"
          class="w-full sm:w-96"
        />
      </template>
    </UPageHero>

    <UContainer class="pb-24">
      <!-- Suggestions -->
      <section
        aria-labelledby="help-suggestions-heading"
        class="grid grid-cols-1 gap-8 rounded-2xl border border-default bg-elevated/40 p-6 shadow-(--surface-shadow) sm:p-8 lg:grid-cols-2"
      >
        <div class="flex flex-col items-start">
          <div class="flex size-10 items-center justify-center rounded-lg bg-primary/10">
            <UIcon
              name="i-lucide-lightbulb"
              class="size-5 text-primary"
            />
          </div>
          <h2
            id="help-suggestions-heading"
            class="mt-4 text-xl font-semibold text-highlighted sm:text-2xl"
          >
            Suggestions
          </h2>
          <p class="mt-2 text-muted">
            Have an idea for mychar.gg? Post it, vote on ideas from other users, and follow each one from open to completed. The most upvoted suggestions rise to the top.
          </p>
          <UButton
            label="Browse suggestions"
            trailing-icon="i-lucide-arrow-right"
            color="primary"
            to="/suggestions"
            class="mt-6"
          />
        </div>

        <!-- min-w-0: long (truncated) titles must not stretch the column past the section -->
        <div class="min-w-0">
          <p class="mb-3 text-xs font-semibold uppercase tracking-wide text-dimmed">
            Popular right now
          </p>

          <div
            v-if="popularStatus === 'pending' || popularStatus === 'idle'"
            class="space-y-2"
          >
            <USkeleton
              v-for="i in 3"
              :key="i"
              class="h-14 w-full rounded-xl"
            />
          </div>

          <ul
            v-else-if="popular?.suggestions.length"
            class="space-y-2"
          >
            <li
              v-for="suggestion in popular.suggestions"
              :key="suggestion.id"
            >
              <NuxtLink
                :to="`/suggestions/${suggestion.id}`"
                class="flex items-center gap-3 rounded-xl border border-default bg-default/60 px-3 py-2.5 transition-colors hover:border-accented"
              >
                <SuggestionScore
                  :score="suggestion.score"
                  class="w-10 shrink-0 text-center text-sm"
                />
                <span class="min-w-0">
                  <span class="block truncate text-sm font-medium text-highlighted">{{ suggestion.title }}</span>
                  <span class="block text-xs text-dimmed">{{ getSuggestionCategory(suggestion.category).label }}</span>
                </span>
              </NuxtLink>
            </li>
          </ul>

          <p
            v-else-if="popularStatus === 'success'"
            class="rounded-xl border border-dashed border-default p-6 text-center text-sm text-muted"
          >
            No suggestions yet. Be the first to share an idea.
          </p>
        </div>
      </section>

      <!-- Docs sections -->
      <section
        aria-labelledby="help-docs-heading"
        class="mt-16"
      >
        <div class="mb-6 flex items-end justify-between gap-4">
          <h2
            id="help-docs-heading"
            class="text-xl font-semibold text-highlighted sm:text-2xl"
          >
            Documentation
          </h2>
          <ULink
            to="/docs/getting-started"
            class="text-sm font-medium text-primary hover:underline"
          >
            Start with the basics
          </ULink>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div
            v-for="section in sections"
            :key="section.path"
            class="flex flex-col rounded-2xl border border-default bg-elevated/40 p-5 shadow-(--surface-shadow)"
          >
            <div class="flex items-center gap-3">
              <div class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <UIcon
                  :name="section.icon"
                  class="size-5 text-primary"
                />
              </div>
              <h3 class="font-semibold text-highlighted">
                <NuxtLink
                  :to="section.path"
                  class="transition-colors hover:text-primary"
                >
                  {{ section.title }}
                </NuxtLink>
              </h3>
            </div>

            <p
              v-if="section.description"
              class="mt-3 line-clamp-3 text-sm text-muted"
            >
              {{ section.description }}
            </p>

            <ul
              v-if="section.pages.length"
              class="mt-4 space-y-1.5 text-sm"
            >
              <li
                v-for="page in section.pages.slice(0, PAGES_PER_SECTION)"
                :key="page.path"
              >
                <NuxtLink
                  :to="page.path"
                  class="flex items-center gap-1.5 text-toned transition-colors hover:text-primary"
                >
                  <UIcon
                    name="i-lucide-chevron-right"
                    class="size-3.5 shrink-0 text-dimmed"
                  />
                  <span class="truncate">{{ page.title }}</span>
                </NuxtLink>
              </li>
            </ul>

            <NuxtLink
              :to="section.path"
              class="mt-auto pt-4 text-sm font-medium text-primary hover:underline"
            >
              {{ section.pages.length > PAGES_PER_SECTION ? `All ${section.pages.length + 1} guides` : 'Read the guide' }}
            </NuxtLink>
          </div>
        </div>
      </section>

      <!-- Contact -->
      <section
        aria-labelledby="help-contact-heading"
        class="mt-16"
      >
        <h2
          id="help-contact-heading"
          class="mb-6 text-xl font-semibold text-highlighted sm:text-2xl"
        >
          Still need help?
        </h2>
        <div class="grid gap-4 sm:grid-cols-2">
          <UPageCard
            title="Ask on Discord"
            description="Ask questions and get help in the mychar.gg Discord server."
            icon="i-simple-icons-discord"
            :to="discordInviteUrl"
            target="_blank"
            variant="outline"
          />
          <UPageCard
            title="Email support"
            description="Write to support@mychar.gg about your account, billing, or anything else."
            icon="i-lucide-mail"
            to="mailto:support@mychar.gg"
            variant="outline"
          />
        </div>
      </section>
    </UContainer>
  </div>
</template>
